import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { keyframes, styled } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CanceledError } from "axios";
import PaginationParameters from "common/contracts/pagination-parameters";
import AHBoolean from "common/values/boolean/boolean";
import Guid from "common/values/guid/guid";
import Individual from "marketplace/entities/individual/individual";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useSession } from "users/session/session-context";
import CommentThread from "work/entities/comment-thread/comment-thread";
import {
  FreelyPatchableFields,
  ProposalField,
} from "work/entities/proposal/proposal";
import FieldRedline, {
  FieldRedlineArray,
} from "work/entities/proposal/redlining/field-redline";
import ProposalRedline from "work/entities/proposal/redlining/proposal-redline";
import SwitchRedline from "work/entities/proposal/redlining/view/redline-field-inputs/switch-redline";
import TeamRedlineSelection from "work/entities/proposal/redlining/view/redline-field-inputs/team-redline-selection";
import WorkTeamTemplateQuery, { CreatorType, WorkTeamTemplateFilterParameters, WorkTeamTemplateOrderParameters } from "work/entities/team-template/api/request-contracts/query-work-team-template-api-request";
import TeamTemplateAPIService from "work/entities/team-template/api/team-template-service";
import TeamTemplate from "work/entities/team-template/team-template";
import ProposalIssues, { ProposalIssueLevel } from "work/values/proposal-issues/proposal-issues";
import { Masked } from "../../../../work-agreement/work-agreement";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ListContainer = styled(Grid)(
  () => ({
    minHeight: "20rem",
  }));
const TemplateContainer = styled(Grid)(
  ({ theme }) => ({
    height: "25rem",
    overflow: "auto",
    [theme.breakpoints.down("lg")]: {
      width: "100%",
    }
  }));
const TemplateList = styled(List)(
  ({ theme }) => ({
    marginRight: theme.spacing(2),
    maxHeight: "25rem",
    minWidth: "20rem",
    overflowY: "auto",
  }));
const TeamGrid = styled(Grid)(
  () => ({
    height: "25rem",
    overflow: "auto",
  }));
const TemplateLoader = styled(CircularProgress)(
  ({ theme }) => ({
    color: theme.palette.primary.main,
    margin: theme.spacing(2),
  }));
const RedlineTeamSelect = styled(TeamRedlineSelection)(
  ({ theme }) => ({
    [theme.breakpoints.down("lg")]: {
      marginTop: theme.spacing(2),
    },
    marginLeft: theme.spacing(2),
  }));
const HeadControls = styled("div")(
  ({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    paddingLeft: theme.spacing(2),
  }));
const Change = styled(
  "div",
  { shouldForwardProp: (prop) => prop !== 'active' }
)<{ active: boolean }>(
  ({ theme, active }) => ({
    animation: active ? `${pulseBorder} 1.5s infinite` : "none",
    border: active ? "1px solid" : 0,
    borderColor: theme.palette.primary.main,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(2),
  }));
const IssueContainer = styled("div")<{ active?: boolean }>(
  ({ theme, active }) => ({
    animation: active ? `${pulseBorder} 1.5s ease-in-out infinite` : "none",
    margin: active ? theme.spacing(1) : 'initial',
    marginLeft: 0,
    marginTop: active ? 0 : theme.spacing(1),
    padding: active ? theme.spacing(1) : "initial",
    borderRadius: theme.shape.borderRadius,
  }));

type TeamTabProps = {
  isOpen: boolean;
  proposalRedline: ProposalRedline;
  issues?: ProposalIssues;
  activeReviewField: ProposalField | undefined;
  commentThreads: CommentThread[];
  disableEditing?: boolean;
  onProposalRedlineChange: (
    newRedline: ProposalRedline,
    traversalFieldOverride?: ProposalField | null
  ) => void;
  onTraverseToNewField?: (fieldInfo: ProposalField | undefined) => void;
  onCommentsClicked: (
    field: ProposalField,
    title?: string
  ) => void;

  fieldsToPatch?: FreelyPatchableFields;

  teamLeader?: Individual;
  onTeamTemplatesUpdated: (newTemplateIds: Guid[]) => void;
  onTeamMemberQuitProposal: (memberId: Guid) => void;
};

const TeamTab = (props: Readonly<TeamTabProps>) => {
  const {
    isOpen,
    proposalRedline,
    activeReviewField,
    commentThreads,
    disableEditing,
    onProposalRedlineChange,
    onTraverseToNewField,
    onCommentsClicked,

    teamLeader,
    fieldsToPatch,
    onTeamTemplatesUpdated,
    onTeamMemberQuitProposal,
  } = props;

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [templateTeams, setTemplateTeams] = React.useState<TeamTemplate[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = React.useState<Guid[]>(
    []
  );

  const issues = React.useMemo(
    () => props.issues?.entries.filter(issue => [
      ProposalField.Team,
      ProposalField.TeamRestriction,
      ProposalField.TeamLeader,
      ProposalField.TeamMember(issue.field.id ?? Guid.empty)
    ].includes(issue.field)),
    [props.issues?.entries]
  );

  const session = useSession();
  const isLargeDownDisplay = useMediaQuery((theme) =>
    theme.breakpoints.down("lg")
  );

  useEffect(
    () => {
      if (!isOpen) return;

      let abortController = new AbortController();
      void getTemplateTeams(abortController);
      setSelectedTemplateIds(fieldsToPatch?.teamTemplateIds ?? []);

      return () => {
        abortController.abort();
        abortController = new AbortController();
      };
    },
    [isOpen]
  );

  async function getTemplateTeams(
    abortController: AbortController
  ): Promise<void> {
    const entityId = session.currentEntity.id;
    if (!entityId) return;

    try {
      setIsLoading(true);
      const teamTemplateService = new TeamTemplateAPIService(session);
      const filterParameters = new WorkTeamTemplateFilterParameters(
        entityId,
        undefined,
        undefined,
        undefined,
        undefined,
        session.context?.viewingAsVendor
          ? CreatorType.Vendor
          : CreatorType.Client);
      const orderParameters = new WorkTeamTemplateOrderParameters();
      const paginationParameters = new PaginationParameters(0, 25);
      const query = new WorkTeamTemplateQuery(
        filterParameters,
        orderParameters,
        paginationParameters
      );

      const templates = await teamTemplateService.getTeamTemplates(
        query,
        abortController
      );

      setTemplateTeams(templates.data);
    } catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
    }
    setIsLoading(false);
  }

  function handleTemplateExpanded(template: TeamTemplate) {
    const templates = [...templateTeams];
    let targetTemplate = templates.find((team) =>
      team.id?.isEqualTo(template.id)
    );
    template.isExpanded = !targetTemplate?.isExpanded;

    setTemplateTeams(templates);
  }

  async function handleTemplateSelectionToggled(
    template: TeamTemplate
  ): Promise<void> {
    if (Masked.isMasked(proposalRedline.team)) {
      throw new Error("You do not have permission to edit the team members.");
    }
    try {
      if (!template.id) {
        console.error(new Error("No template selected"));
        return;
      }
      let updatedSelectedTemplateIds = [...selectedTemplateIds];

      const isAlreadySelected = selectedTemplateIds.some((id) =>
        id.isEqualTo(template.id)
      );
      if (isAlreadySelected) {
        updatedSelectedTemplateIds = updatedSelectedTemplateIds.filter(
          (id) => !id.isEqualTo(template.id)
        );
      } else {
        let newTeamMembersRedline: FieldRedlineArray<Individual> =
          proposalRedline.team;
        template.members.forEach((member: Individual) => {
          if (!getIsMemberAlreadySelected(member)) {
            newTeamMembersRedline = newTeamMembersRedline.addEntry(member);
          }
        });
        const newProposalRedline = proposalRedline.updateTeamMembersRedline(
          newTeamMembersRedline
        );
        onProposalRedlineChange(
          newProposalRedline,
          null
        );
        updatedSelectedTemplateIds.push(template.id);
      }
      setSelectedTemplateIds(updatedSelectedTemplateIds);
      onTeamTemplatesUpdated(updatedSelectedTemplateIds);
      onCommentsClicked(
        ProposalField.Team,
        "Team"
      );
    } catch (err) {
      console.error(err);
      enqueueSnackbar(
        "Failed to apply template. Please try again",
        {
          variant: "error",
        }
      );
    } finally {
      setIsLoading(false);
    }
  }

  function handleToggleTeamMemberSelection(member: Individual) {
    if (Masked.isMasked(proposalRedline.team)) {
      throw new Error("You do not have permission to edit the team members.");
    }

    if (getIsMemberAlreadySelected(member)) {
      onProposalRedlineChange(
        proposalRedline.updateTeamMembersRedline(
          proposalRedline.team.removeEntryByFieldId(member?.id ?? undefined),
        ),
        null
      );
    } else {
      onProposalRedlineChange(
        proposalRedline.updateTeamMembersRedline(
          proposalRedline.team.addEntry(member)
        ),
        null
      );
    }
    onCommentsClicked(
      ProposalField.Team,
      "Team"
    );
  }

  function getIsMemberAlreadySelected(member: Individual): boolean {
    if (Masked.isMasked(proposalRedline.team)) return false;
    return (
      (teamLeader?.isEqualTo(member) ?? false) ||
      proposalRedline.team.redlines.some((individualRedline) =>
        individualRedline.currentEntry
          ? individualRedline.currentEntry.isEqualTo(member)
          : false
      )
    );
  }

  function renderTemplates() {
    if (Masked.isMasked(proposalRedline.team)) {
      return "You do not have permission to view teams.";
    }
    return (
      <TemplateList subheader={<ListSubheader>Templates</ListSubheader>}>
        {templateTeams.length === 0 && (
          <>
            {isLoading ? (
              <TemplateLoader size={32} thickness={4} disableShrink />
            ) : (
              <ListItem key="noTemplates">
                <ListItemText>No Templates</ListItemText>
              </ListItem>
            )}
          </>
        )}
        {templateTeams.map((template: TeamTemplate) => {
          const isSelected = selectedTemplateIds.some((id) =>
            id.isEqualTo(template.id)
          );

          if (Masked.isMasked(proposalRedline.team)) {
            throw new Error("You do not have permission to edit the team members.");
          }

          return (
            <div key={template.id?.value}>
              <ListItem
                secondaryAction={
                  <Tooltip
                    title={`${template.isExpanded ? "Hide" : "Show"} Members`}
                  >
                    <span>
                      <IconButton
                        edge="end"
                        disabled={!proposalRedline.team?.isResolved}
                        onClick={() => handleTemplateExpanded(template)}
                        size="medium"
                      >
                        {template.isExpanded ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                }
              >
                <ListItemIcon>
                  <Tooltip
                    title={
                      !isSelected ? "Select Template" : "Deselect Template"
                    }
                  >
                    <span>
                      <Checkbox
                        edge="start"
                        color="primary"
                        checked={isSelected}
                        indeterminate={
                          !isSelected &&
                          (template.userIsReferenced(teamLeader?.userId) ||
                            proposalRedline.team.redlines
                              .map((memberRedline) => memberRedline.field.id)
                              .some((memberId) =>
                                template.userIsReferenced(memberId ?? undefined)
                              ))
                        }
                        tabIndex={-1}
                        disableRipple
                        onChange={() =>
                          handleTemplateSelectionToggled(template)
                        }
                      />
                    </span>
                  </Tooltip>
                </ListItemIcon>
                <ListItemText>{template.name.value}</ListItemText>
              </ListItem>
              <Collapse
                in={template.isExpanded}
                timeout="auto"
                sx={{ backgroundColor: "#FAFAFA" }}
              >
                <Divider />
                <List
                  disablePadding
                  dense
                  subheader={<ListSubheader>Leader</ListSubheader>}
                >
                  {renderTemplateLeader(template)}
                </List>
                <List
                  disablePadding
                  dense
                  subheader={<ListSubheader>Members</ListSubheader>}
                >
                  {renderTemplateMembers(template)}
                </List>
                <Divider />
              </Collapse>
            </div>
          );
        })}
      </TemplateList>
    );
  }

  function renderTemplateLeader(template: TeamTemplate) {
    return (
      <ListItem
        key="teamLeader"
        secondaryAction={
          <Checkbox
            edge="end"
            color="primary"
            disabled
            checked={Boolean(
              teamLeader?.userId?.isEqualTo(template?.leader?.userId)
            )}
            tabIndex={-1}
            disableRipple
          />
        }
      >
        <ListItemText>{template.leader.getFullName()}</ListItemText>
      </ListItem>
    );
  }

  function renderTemplateMembers(template: TeamTemplate) {
    if (Masked.isMasked(proposalRedline.team)) {
      return "You do not have permission to view team members.";
    }

    return template?.members?.map((member: Individual) => {
      if (Masked.isMasked(proposalRedline.team)) {
        throw new Error("You do not have permission to edit the team members.");
      }

      return (
        <ListItem
          key={member.userId?.value}
          onClick={() => handleToggleTeamMemberSelection(member)}
          secondaryAction={
            <Checkbox
              edge="end"
              color="primary"
              checked={proposalRedline.team?.redlines.some((m) =>
                m.currentEntry?.userId?.isEqualTo(member.userId)
              )}
              tabIndex={-1}
              disableRipple
              onClick={() => handleToggleTeamMemberSelection(member)}
            />
          }
        >
          <ListItemText
            style={{ whiteSpace: "pre-line" }}
            primary={`${member.profile?.firstName} ${member.profile?.lastName}`}
          />
        </ListItem>
      )
    });
  }

  function handleTeamRestrictedChanged(
    newRedline: FieldRedline<AHBoolean>,
    traversalFieldOverride?: ProposalField | null
  ) {
    try {
      onProposalRedlineChange(
        proposalRedline.updateTeamRestrictedRedline(newRedline),
        traversalFieldOverride
      );
      onCommentsClicked(
        ProposalField.Team,
        "Team"
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Error updating vendor-team restriction",
        { variant: "error" }
      );
    }
  }

  if (!isOpen) return null;

  if (Masked.isMasked(proposalRedline.team) ||
    Masked.isMasked(proposalRedline.teamRestricted)) {
    return "You do not have permission to view this team.";
  }

  return (
    <>
      <HeadControls>
        {(issues?.length ?? 0) > 0 && (
          <IssueContainer>
            {issues?.map((issue) => (
              <Typography
                key={issue.field?.key}
                color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                variant="body1"
              >
                {issue.description}
              </Typography>
            ))}
          </IssueContainer>
        )}
        <Change
          active={
            activeReviewField?.isEqualTo(ProposalField.TeamRestriction) ?? false
          }
        >
          <SwitchRedline
            boolRedline={proposalRedline?.teamRestricted}
            disabled={disableEditing}
            promptTitleText="Restrict team?"
            promptMessageText="Restrict team members from freely leaving team without triggering a renegotiation?"
            okButtonText="Restrict Team"
            unsetLabel="Team Not Restricted"
            setLabel="Team Restricted"
            onBoolRedlineChange={(newRedline, traversalFieldOverride) => {
              try {
                handleTeamRestrictedChanged(
                  newRedline,
                  traversalFieldOverride
                );
              } catch (error) {
                console.error(error);
                enqueueSnackbar(
                  "Error updating vendor-team restriction",
                  {
                    variant: "error",
                  }
                );
              }
            }}
          />
        </Change>
      </HeadControls>
      <ListContainer container direction="row">
        {!disableEditing && (
          <>
            <TemplateContainer>
              {renderTemplates()}
              {isLargeDownDisplay && <Divider />}
            </TemplateContainer>
            {!isLargeDownDisplay && (
              <Grid>
                <Divider orientation="vertical" />
              </Grid>
            )}
          </>
        )}
        <TeamGrid size="grow">
          <RedlineTeamSelect
            activeReviewField={activeReviewField}
            commentThreads={commentThreads}
            disableEditing={disableEditing}
            teamLeader={teamLeader}
            teamMembersRedline={proposalRedline.team}
            onCommentsClicked={onCommentsClicked}
            onTeamMembersRedlineChange={(
              newTeamRedline: FieldRedlineArray<Individual>,
              traversalFieldOverride?: ProposalField | null
            ) => {
              try {
                onProposalRedlineChange(
                  proposalRedline.updateTeamMembersRedline(newTeamRedline),
                  traversalFieldOverride
                );
                onCommentsClicked(
                  ProposalField.Team,
                  "Team"
                );
              } catch (error) {
                console.error(error);
                enqueueSnackbar(
                  "Error updating vendor-team members",
                  {
                    variant: "error",
                  }
                );
              }
            }}
            onMemberQuitTeam={(memberId: Guid) => {
              try {
                onTeamMemberQuitProposal(memberId);
              } catch (error) {
                console.error(error);
                enqueueSnackbar(
                  "Error quitting vendor-team",
                  { variant: "error" }
                );
              }
            }}
            onMemberClicked={(memberId: Guid) => {
              onTraverseToNewField?.(ProposalField.TeamMember(memberId));
            }}
          />
        </TeamGrid>
      </ListContainer>
    </>
  );
};

export default TeamTab;
